// E22-t2 (slice 2) — THE ENVELOPE, PARSED STRICTLY, AND THE CLOCK ARITHMETIC.
//
// Contract: vault/Flows/Scheduled Reports.md — f1: "the envelope is the only
//   rigid part": a report document's frontmatter declares cadence, delivery
//   and the per-run token cap in a FIXED schema. This module is that schema's
//   parser and nothing more: it reads exactly the envelope, refuses anything
//   it does not understand WITH THE REASON (an armed report with an unreadable
//   envelope surfaces q3-style — a visible failure, never a silent skip), and
//   never sees the body — indicators, instructions and composites are the
//   run engine's subject, not the scheduler's.
//
//   WHY NOT THE ENGINE'S parseDoc: that parses a document against the KB's
//   whole compiled format — the referee's job, done at push. The scheduler
//   needs three fields off a fenced frontmatter block on every alarm, in the
//   Durable Object, without loading a format; a strict reader for a fixed
//   schema IS the contract here, not a yaml shortcut. The referee remains the
//   authority on the document; this only decides when to wake.
//
// Contract: f3 — "a missed window runs once": isDue answers from the LAST RUN
//   (or the arm time for a never-run report), so two elapsed windows are one
//   due run, and nextDueAt after that run points forward, never backward.
//
// All arithmetic is UTC. `at` is "HH:MM" 24h; `day` is mon..sun for weekly and
// 1..28 for monthly (28 caps it so every month has the day — the envelope
// refuses 29–31 rather than guessing what February means).

export interface ReportSchedule {
  every: "daily" | "weekly" | "monthly";
  /** weekly: 0–6 (sun..sat, Date.getUTCDay's vocabulary); monthly: 1–28 */
  day: number | null;
  /** minutes past UTC midnight */
  atMinutes: number;
  /** per-run agent-token cap (flow q3, ratified): null = the org default */
  tokenCap: number | null;
  /** delivery targets; empty = snapshot only */
  delivery: ReadonlyArray<"socket" | "email">;
  /**
   * E33-t3 — WHAT A DUE FIRE RUNS. ABSENT is today's behaviour verbatim: the
   * stamped R2 indicator snapshot (f4/f7). "surface" names the E32 report
   * SURFACE as the runnable — on fire the KB's Durable Object re-bakes the
   * format's report artifact at its established key instead of landing a
   * snapshot. OPTIONAL AND ABSENT-BY-DEFAULT on purpose: an envelope that
   * does not say `runs:` parses to the exact shape it always did, so no
   * existing schedule, arm row or org-screen row moves a byte. An explicit
   * `runs: snapshot` normalizes to absence — one spelling in the parsed
   * output, two admitted on the page.
   */
  runs?: "surface";
  /**
   * surface runs only: the FORMAT whose report surface bakes (a KB holds
   * several formats and the report surface is per-format — E32). null means
   * the KB's designated primary format, resolved at fire time by the DO,
   * which is the only party that can see the kb_record.
   */
  surfaceFormat?: string | null;
}

export interface ReportEnvelope {
  uuid: string;
  title: string;
  schedule: ReportSchedule;
}

export type EnvelopeResult =
  | { ok: true; envelope: ReportEnvelope }
  | { ok: false; reason: string };

const WEEKDAYS: Record<string, number> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

/** Strip one level of matching quotes — the envelope admits both spellings. */
const unquote = (value: string): string => {
  const v = value.trim();
  if (v.length >= 2 && ((v[0] === '"' && v.endsWith('"')) || (v[0] === "'" && v.endsWith("'")))) {
    return v.slice(1, -1);
  }
  return v;
};

/**
 * The frontmatter block as flat pairs plus ONE nested map per key — exactly
 * the shapes the envelope schema uses, refused loudly beyond them. Returns
 * null when the document has no frontmatter fence at all.
 */
function frontmatterPairs(
  source: string,
): { flat: Map<string, string>; nested: Map<string, Map<string, string>> } | null {
  if (!source.startsWith("---")) return null;
  const end = source.indexOf("\n---", 3);
  if (end < 0) return null;
  const block = source.slice(source.indexOf("\n") + 1, end);
  const flat = new Map<string, string>();
  const nested = new Map<string, Map<string, string>>();
  let openKey: string | null = null;
  for (const line of block.split("\n")) {
    if (line.trim() === "" || line.trim().startsWith("#")) continue;
    const indented = /^\s+\S/.test(line);
    if (indented && openKey !== null) {
      const m = line.trim().match(/^([\w-]+)\s*:\s*(.*)$/);
      if (m) nested.get(openKey)?.set(m[1], unquote(m[2]));
      continue;
    }
    const m = line.match(/^([\w-]+)\s*:\s*(.*)$/);
    if (!m) continue;
    if (m[2].trim() === "") {
      openKey = m[1];
      nested.set(openKey, new Map());
    } else {
      openKey = null;
      flat.set(m[1], unquote(m[2]));
    }
  }
  return { flat, nested };
}

/** Is this document a report at all? Cheap, no refusals: discovery filters
 *  with this, and only what it admits is held to the envelope schema. */
export function isReportDoc(source: string): boolean {
  const pairs = frontmatterPairs(source);
  return pairs !== null && pairs.flat.get("type") === "report";
}

/** The envelope, or the reason it cannot be one. Every refusal names the
 *  field, because the sentence lands on the org screen (q3: visible). */
export function parseReportEnvelope(source: string): EnvelopeResult {
  const pairs = frontmatterPairs(source);
  if (pairs === null) return { ok: false, reason: "no frontmatter block" };
  const { flat, nested } = pairs;
  if (flat.get("type") !== "report") return { ok: false, reason: "type is not 'report'" };
  const uuid = flat.get("uuid") ?? "";
  if (uuid === "") return { ok: false, reason: "uuid: missing — a report's identity is its uuid" };
  const title = flat.get("title") ?? uuid;

  const schedule = nested.get("schedule");
  if (!schedule) return { ok: false, reason: "schedule: missing from the frontmatter envelope" };

  const every = schedule.get("every") ?? "";
  if (every !== "daily" && every !== "weekly" && every !== "monthly") {
    return { ok: false, reason: `schedule.every: '${every}' is not daily | weekly | monthly` };
  }

  const at = schedule.get("at") ?? "";
  const atMatch = at.match(/^(\d{1,2}):(\d{2})$/);
  const hours = atMatch ? Number(atMatch[1]) : NaN;
  const minutes = atMatch ? Number(atMatch[2]) : NaN;
  if (!atMatch || hours > 23 || minutes > 59) {
    return { ok: false, reason: `schedule.at: '${at}' is not HH:MM (24h, UTC)` };
  }

  let day: number | null = null;
  if (every === "weekly") {
    const raw = (schedule.get("day") ?? "").toLowerCase();
    if (!(raw in WEEKDAYS)) {
      return { ok: false, reason: `schedule.day: '${raw}' is not mon..sun (weekly needs one)` };
    }
    day = WEEKDAYS[raw];
  }
  if (every === "monthly") {
    const raw = Number(schedule.get("day") ?? NaN);
    if (!Number.isInteger(raw) || raw < 1 || raw > 28) {
      return {
        ok: false,
        reason: `schedule.day: '${schedule.get("day") ?? ""}' is not 1..28 (28 caps it so every month has the day)`,
      };
    }
    day = raw;
  }

  let tokenCap: number | null = null;
  const capRaw = schedule.get("token_cap");
  if (capRaw !== undefined) {
    const cap = Number(capRaw);
    if (!Number.isInteger(cap) || cap <= 0) {
      return { ok: false, reason: `schedule.token_cap: '${capRaw}' is not a positive integer` };
    }
    tokenCap = cap;
  }

  const deliveryRaw = schedule.get("delivery") ?? "";
  const delivery: Array<"socket" | "email"> = [];
  if (deliveryRaw !== "") {
    for (const part of deliveryRaw.replace(/^\[|\]$/g, "").split(",")) {
      const target = unquote(part).trim();
      if (target === "") continue;
      if (target !== "socket" && target !== "email") {
        return { ok: false, reason: `schedule.delivery: '${target}' is not socket | email` };
      }
      delivery.push(target);
    }
  }

  // E33-t3 — THE RUNNABLE. The smallest honest extension the ticket asked
  // for: ONE field naming what a due fire produces, absent meaning exactly
  // what every schedule already did. `surface` may name a format explicitly
  // (`surface: <name>`); a bare `runs: surface` leaves the format null and
  // the DO resolves it to the KB's primary at fire time — the parser stays
  // pure over the document and never guesses at control-plane state. A
  // `surface:` line on a snapshot schedule is a contradiction, refused with
  // the reason rather than ignored: the author asked for something this
  // schedule will not do, and a silent skip is how a report bakes nowhere
  // for a month before anyone reads the envelope again (q3's visibility).
  const runsRaw = schedule.get("runs");
  let runs: "surface" | undefined;
  if (runsRaw !== undefined && runsRaw !== "snapshot") {
    if (runsRaw !== "surface") {
      return { ok: false, reason: `schedule.runs: '${runsRaw}' is not snapshot | surface` };
    }
    runs = "surface";
  }
  let surfaceFormat: string | null = null;
  const surfaceRaw = schedule.get("surface");
  if (surfaceRaw !== undefined) {
    if (runs !== "surface") {
      return {
        ok: false,
        reason: "schedule.surface: only a `runs: surface` schedule names a surface format — this schedule runs the snapshot",
      };
    }
    if (surfaceRaw === "") {
      return {
        ok: false,
        reason: "schedule.surface: empty — name a format, or omit the line to bake the primary format's report surface",
      };
    }
    surfaceFormat = surfaceRaw;
  }

  return {
    ok: true,
    envelope: {
      uuid,
      title,
      schedule: {
        every,
        day,
        atMinutes: hours * 60 + minutes,
        tokenCap,
        delivery,
        // conditional spread, not `runs: undefined`: the parsed shape of a
        // runnable-free envelope is FROZEN by platform/test/report-schedule
        // (deep equality) and by every consumer that serializes it verbatim.
        ...(runs === "surface" ? { runs, surfaceFormat } : {}),
      },
    },
  };
}

/** The next due instant STRICTLY AFTER `afterIso`, in UTC. */
export function nextDueAt(schedule: ReportSchedule, afterIso: string): string {
  const after = new Date(afterIso).getTime();
  const candidate = new Date(afterIso);
  candidate.setUTCHours(Math.floor(schedule.atMinutes / 60), schedule.atMinutes % 60, 0, 0);

  const step = (): void => {
    if (schedule.every === "daily") {
      candidate.setUTCDate(candidate.getUTCDate() + 1);
      return;
    }
    if (schedule.every === "weekly") {
      candidate.setUTCDate(candidate.getUTCDate() + 1);
      return; // stepped daily; the weekday filter below decides
    }
    // monthly: next day-of-month occurrence
    candidate.setUTCMonth(candidate.getUTCMonth() + 1);
  };

  if (schedule.every === "monthly") {
    candidate.setUTCDate(schedule.day ?? 1);
    while (candidate.getTime() <= after) step();
    return candidate.toISOString();
  }

  // daily/weekly: walk forward a day at a time until both the instant and (for
  // weekly) the weekday agree. Bounded by 8 steps by construction.
  while (
    candidate.getTime() <= after ||
    (schedule.every === "weekly" && candidate.getUTCDay() !== (schedule.day ?? 0))
  ) {
    step();
  }
  return candidate.toISOString();
}

/**
 * Due NOW? Asked against the last settled run (or the arm time for a report
 * that has never run): due when the next occurrence after that anchor is not
 * in the future. Two missed windows still answer ONE due run (f3/s5), because
 * the anchor only moves when a run is recorded.
 */
export function isDue(schedule: ReportSchedule, anchorIso: string, nowIso: string): boolean {
  return new Date(nextDueAt(schedule, anchorIso)).getTime() <= new Date(nowIso).getTime();
}

const WEEKDAY_LABELS = ["Sundays", "Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays"];

/** The schedule as the org screen says it — one spelling, shared, so the
 *  table and the report document can never phrase the same cadence apart. */
export function scheduleLabel(schedule: ReportSchedule): string {
  const hh = String(Math.floor(schedule.atMinutes / 60)).padStart(2, "0");
  const mm = String(schedule.atMinutes % 60).padStart(2, "0");
  const at = `${hh}:${mm} UTC`;
  if (schedule.every === "daily") return `Every day ${at}`;
  if (schedule.every === "weekly") return `${WEEKDAY_LABELS[schedule.day ?? 0]} ${at}`;
  return `Monthly on day ${schedule.day ?? 1}, ${at}`;
}
